/* eslint-disable @typescript-eslint/no-explicit-any */
import { vec3, ReadonlyMat4, ReadonlyVec3 } from 'gl-matrix';
import { roundToDecimals } from '../dicom/utils';
import { DomainValidationError } from '~/domain/errors/DomainError';
import { REFORMAT_DERIVATION_BASE } from './imageService/derivationDescription';
import { Grid } from '~/engine/data/common/Grid';
import AppConfig from '~/utils/appconfig';
import { TransferSyntaxUID } from './dicomProcessor';
import { generateUID } from '~/utils/dicomUid';

export interface VolumeGeometry {
    size: ReadonlyVec3; // [nx, ny, nz]
    res: ReadonlyVec3; // [dx, dy, dz] mm   (square in-plane from resampler)
    transform: ReadonlyMat4; // world = transform * [i, j, k, 1]
}

/**
 * The rescale the derived series is written under, or null when it carries
 * none and its stored values ARE its output values.
 *
 * ONE resolution, because two decide the same thing and must agree: the
 * pixels are reversed through this rescale, and this rescale is what the
 * output declares. Two copies could write an image encoded under one
 * rescale and declare another, which is a silently wrong image with
 * nothing to say so.
 *
 * A CT with either attribute missing takes the conventional 1 / -1024.
 * Where exactly one of the two is missing on any other modality it is
 * filled from the DICOM default a reader would itself assume — slope 1,
 * intercept 0 — which is also what `DicomImageSlice` used to produce the
 * values being written back. Resolving only one of them meant the writer
 * declared a slope it had not applied, scaling the output by it.
 */
function resolveRescale(
    exampleDataset: any
): { slope: number; intercept: number } | null {
    const isCt = exampleDataset.Modality?.toUpperCase() === 'CT';
    const given = (value: unknown, fallback: number): number | undefined =>
        value === undefined || value === null ? fallback : Number(value);

    const hasSlope =
        exampleDataset.RescaleSlope !== undefined &&
        exampleDataset.RescaleSlope !== null;
    const hasIntercept =
        exampleDataset.RescaleIntercept !== undefined &&
        exampleDataset.RescaleIntercept !== null;

    // Neither given, and not a CT: the stored values are the output values.
    if (!hasSlope && !hasIntercept && !isCt) {
        return null;
    }

    const slope = given(exampleDataset.RescaleSlope, 1) as number;
    const intercept = given(
        exampleDataset.RescaleIntercept,
        isCt ? -1024 : 0
    ) as number;

    // A zero or unparseable slope gives Infinity or NaN per voxel, which a
    // clamp would turn into a saturated or an all-zero volume — a silently
    // wrong image rather than a refusal.
    if (!Number.isFinite(slope) || slope === 0 || !Number.isFinite(intercept)) {
        throw new Error(
            'Cannot write derived pixels: RescaleSlope must be a non-zero ' +
                'number and RescaleIntercept must be a number'
        );
    }
    return { slope, intercept };
}

/** Reverse {@link resolveRescale} so the values land in `PixelData`. */
function applyRescaleTransformation(
    pixels: Int16Array,
    exampleDataset: any
): Int16Array {
    const rescale = resolveRescale(exampleDataset);
    if (rescale === null) {
        return pixels;
    }
    const { slope, intercept } = rescale;

    // Reversing the rescale: stored = (value - intercept) / slope.
    const transformedPixels = new Int16Array(pixels.length);

    let lowest = Infinity;
    let highest = -Infinity;
    for (let i = 0; i < pixels.length; i++) {
        // Reverse the rescale transformation to get stored pixel value
        const storedValue = Math.round((pixels[i] - intercept) / slope);
        if (storedValue < lowest) {
            lowest = storedValue;
        }
        if (storedValue > highest) {
            highest = storedValue;
        }
        transformedPixels[i] = storedValue;
    }

    // Refused rather than saturated. Clamping here would have written a
    // different image from the one resampled, with nothing to say so — and
    // the values it would alter are exactly the extremes a dose
    // calculation reads as dense material.
    if (lowest < -32768 || highest > 32767) {
        throw new Error(
            `Cannot write derived pixels: stored values ${lowest}..${highest} ` +
                `do not fit the 16-bit signed range under RescaleSlope ` +
                `${slope} and RescaleIntercept ${intercept}`
        );
    }

    return transformedPixels;
}

export interface DerivedSeriesOptions {
    /**
     * When provided, this UID is reused in the derived series instead of
     * generating a new one. Pass the original FoR UID when the derived
     * image lives in the same patient coordinate system, so that existing
     * structure sets and registrations remain associated.
     */
    preserveFrameOfReferenceUid?: string;
    /**
     * Text for `DerivationDescription` (0008,2111). Say what was done to
     * the pixels, including anything in the output that was not measured.
     */
    derivationDescription?: string;
    /**
     * Slices at each end that are copies of their neighbour rather than
     * resampled data.
     *
     * The series-level `DerivationDescription` says how many there are;
     * this is what says WHICH. A recipient who was not present when the
     * series was made can otherwise only infer it from the count and the
     * instance order.
     */
    replicated?: { inferiorSlices: number; superiorSlices: number };
    /**
     * `SeriesNumber (0020,0011)` for the derived series.
     *
     * Required, because the caller is the only layer that can see what
     * numbers the study already holds. Falling back to the source's own
     * number here collided with it on every reformat — the one failure
     * {@link nextFreeSeriesNumber} exists to prevent — and did so silently.
     */
    seriesNumber: number;
}

/**
 * `SeriesDescription (0008,103E)` when the caller supplies none.
 *
 * Short and greppable in a planning system's series list, and it names what
 * the object is rather than what was done to it.
 */
export const DEFAULT_DERIVED_SERIES_DESCRIPTION = 'Axial RFMT';

/** SOP Classes whose IOD defines a term for what this writer produces. */
const CT_IMAGE_STORAGE = '1.2.840.10008.5.1.4.1.1.2';
const MR_IMAGE_STORAGE = '1.2.840.10008.5.1.4.1.1.4';

/**
 * `ImageType (0008,0008)` for the derived series.
 *
 * Values 1 and 2 are the standard's own terms for an image computed from
 * other images rather than acquired. SECONDARY, not PRIMARY: value 2
 * characterises the patient examination, and this image was not part of
 * one — it was made at a workstation afterwards. Some vendors write
 * PRIMARY for a reformat; that is their choice and not this one.
 *
 * Value 3 belongs to the IOD, and the term differs by modality: the CT
 * Image Module defines AXIAL for a cross-sectional image, while the MR
 * Image Module defines MPR for exactly this — a multi-planar reformat.
 * Writing AXIAL under MR would put a term the MR IOD does not define into
 * a slot it owns, so a reader looking for MPR would not find it. Where the
 * IOD defines no suitable term the value is left empty, which the standard
 * permits, rather than asserting one that does not belong to it.
 *
 * Value 4 onward is implementation specific, which is where REFORMATTED
 * can name what produced the image without displacing anything.
 */
export function derivedImageType(sopClassUID: string): string[] {
    const plane =
        sopClassUID === CT_IMAGE_STORAGE
            ? 'AXIAL'
            : sopClassUID === MR_IMAGE_STORAGE
              ? 'MPR'
              : '';
    return ['DERIVED', 'SECONDARY', plane, 'REFORMATTED'];
}

/**
 * Marks an individual slice as a copy. `ImageComments` (0020,4000) is Type 3
 * and varies per slice by design, unlike `ImageType`, whose consistency
 * across the series SRV-WOR-B-1-3-1 asserts.
 */
const REPLICATED_SLICE_COMMENT =
    'Replicated from the boundary slice for scatter. Not acquired anatomy.';

export async function buildDerivedSeriesFromVolume(
    pixels: Int16Array,
    grid: Grid,
    exampleDataset: any,
    options: DerivedSeriesOptions
): Promise<any[]> {
    const nx = grid.sizeX;
    const ny = grid.sizeY;
    const nz = grid.sizeZ;

    if (pixels.length !== nx * ny * nz) {
        throw new Error('Pixel data length does not match geometry.size');
    }

    const T = grid.getTransformation();

    // Extract orientation from grid (supports non-HFS grids)
    const gridCorner = vec3.fromValues(T[12], T[13], T[14]);
    const unitX = grid.getUnitX();
    const unitY = grid.getUnitY();
    const sliceNormal = grid.getUnitZ();

    const rowDir: [number, number, number] = [unitX[0], unitX[1], unitX[2]];
    const colDir: [number, number, number] = [unitY[0], unitY[1], unitY[2]];

    const colSpacing = grid.resX; // dx
    const rowSpacing = grid.resY; // dy
    const sliceThickness = grid.resZ; // dz

    // Grid transformation origin is the corner of the first voxel.
    // DICOM ImagePositionPatient must be the center of the first
    // pixel in each slice: shift by half a voxel in all three axes.
    const ippOrigin = vec3.fromValues(
        gridCorner[0] +
            0.5 * colSpacing * rowDir[0] +
            0.5 * rowSpacing * colDir[0] +
            0.5 * sliceThickness * sliceNormal[0],
        gridCorner[1] +
            0.5 * colSpacing * rowDir[1] +
            0.5 * rowSpacing * colDir[1] +
            0.5 * sliceThickness * sliceNormal[1],
        gridCorner[2] +
            0.5 * colSpacing * rowDir[2] +
            0.5 * rowSpacing * colDir[2] +
            0.5 * sliceThickness * sliceNormal[2]
    );

    // --- Apply rescale transformation to pixel data ---
    const transformedPixels = applyRescaleTransformation(
        pixels,
        exampleDataset
    );

    // --- UIDs & shared attributes ---

    const studyInstanceUID: string =
        exampleDataset.StudyInstanceUID || generateUID();

    const newSeriesInstanceUID: string = generateUID();

    const frameOfReferenceUID: string =
        options.preserveFrameOfReferenceUid || generateUID();

    // The derived series inherits the source's IOD. Falling back to a
    // fixed SOP Class instead would emit, say, a CT under MR Image Storage
    // while `Modality` still said CT — a malformed object that passes a
    // naive reader and fails IOD validation. SOPClassUID is Type 1 in every
    // image IOD, so absence means the source is already malformed.
    const sopClassUID: string = exampleDataset.SOPClassUID;
    if (!sopClassUID) {
        throw new DomainValidationError(
            'Cannot build a derived series: the source image has no SOPClassUID.'
        );
    }

    const imageType = derivedImageType(sopClassUID);

    const pixelRepresentation = exampleDataset.PixelRepresentation ?? 1; // 1 = signed
    const bitsAllocated = exampleDataset.BitsAllocated ?? 16;
    const bitsStored = exampleDataset.BitsStored ?? 16;
    const highBit =
        exampleDataset.HighBit ?? (bitsStored > 0 ? bitsStored - 1 : 15);

    const samplesPerPixel = exampleDataset.SamplesPerPixel ?? 1;
    const photometricInterpretation =
        exampleDataset.PhotometricInterpretation ?? 'MONOCHROME2';

    const rows = ny;
    const columns = nx;

    // The same resolution the pixels were reversed through, so what the
    // output declares and what it holds cannot disagree.
    const outputRescale = resolveRescale(exampleDataset);

    const datasets: any[] = [];
    const sliceSize = nx * ny;

    // Timestamps for derived series creation
    const now = new Date();
    const dateStr =
        `${now.getFullYear()}` +
        `${String(now.getMonth() + 1).padStart(2, '0')}` +
        `${String(now.getDate()).padStart(2, '0')}`;
    const timeStr =
        `${String(now.getHours()).padStart(2, '0')}` +
        `${String(now.getMinutes()).padStart(2, '0')}` +
        `${String(now.getSeconds()).padStart(2, '0')}`;

    // Generated up front so a replicated slice can reference, by UID, the
    // slice it was copied from — which is one of these, not a source image.
    const sopInstanceUIDs = Array.from({ length: nz }, () => generateUID());
    const inferiorReplicas = options.replicated?.inferiorSlices ?? 0;
    const superiorReplicas = options.replicated?.superiorSlices ?? 0;
    const firstDataSlice = inferiorReplicas;
    const lastDataSlice = nz - 1 - superiorReplicas;

    for (let z = 0; z < nz; z++) {
        const sliceOffset = z * sliceSize;
        const sliceView = transformedPixels.subarray(
            sliceOffset,
            sliceOffset + sliceSize
        );
        const sliceBuffer = sliceView.buffer.slice(
            sliceView.byteOffset,
            sliceView.byteOffset + sliceView.byteLength
        );

        // IPP for slice z: voxel-center origin + z * sliceThickness * normal
        const ipp = vec3.fromValues(
            ippOrigin[0] + z * sliceThickness * sliceNormal[0],
            ippOrigin[1] + z * sliceThickness * sliceNormal[1],
            ippOrigin[2] + z * sliceThickness * sliceNormal[2]
        );

        const sopInstanceUID = sopInstanceUIDs[z];
        // A replica is a copy of the nearest slice that holds resampled
        // data; everything between the two ends is resampled.
        const copiedFrom =
            z < firstDataSlice
                ? firstDataSlice
                : z > lastDataSlice
                  ? lastDataSlice
                  : null;

        // Start from a shallow copy of template to keep patient/study fields
        const ds: any = { ...exampleDataset };

        // Remove geometry & pixel-related tags we'll overwrite
        delete ds.PixelData;
        delete ds.Rows;
        delete ds.Columns;
        delete ds.ImagePositionPatient;
        delete ds.ImageOrientationPatient;
        delete ds.PixelSpacing;
        delete ds.SliceThickness;
        delete ds.SOPInstanceUID;
        delete ds.SeriesInstanceUID;
        delete ds.StudyInstanceUID;
        delete ds.FrameOfReferenceUID;
        delete ds.InstanceNumber;
        delete ds.ImageType;
        delete ds.BitsAllocated;
        delete ds.BitsStored;
        delete ds.HighBit;
        delete ds.PixelRepresentation;
        delete ds.SamplesPerPixel;
        delete ds.PhotometricInterpretation;
        delete ds.SpacingBetweenSlices;
        delete ds.SliceLocation;

        // The out-of-source fill is the source's own MINIMUM, which is not
        // necessarily the value this attribute declares, and interpolation
        // near the boundary produces values between the two either way. A
        // dose engine excludes padding-valued voxels from its density
        // grid, so a declaration that no longer matches the pixels would
        // mis-mask the calculation.
        delete ds.PixelPaddingValue;
        delete ds.PixelPaddingRangeLimit;
        // Describe a reconstruction field of view the output does not have.
        delete ds.ReconstructionDiameter;
        delete ds.DataCollectionDiameter;

        // Inherited from the template and false of the output. The shallow
        // copy above carries every attribute of ONE source slice onto every
        // derived slice, so anything the resample invalidates has to go or
        // it becomes a confident statement about the wrong image.
        //
        // GantryDetectorTilt is the one that can misdirect a reader rather
        // than merely mislead: the output is true axial, and a consumer
        // that honours a non-zero tilt would correct geometry that has
        // already been corrected.
        delete ds.GantryDetectorTilt;
        // Extrema of the source's stored values, not of these.
        delete ds.SmallestImagePixelValue;
        delete ds.LargestImagePixelValue;
        // The source slice's own comment, which would land on every
        // derived slice and dilute the marking that identifies a replica.
        delete ds.ImageComments;
        // Identify an acquisition this image is not part of.
        delete ds.AcquisitionNumber;
        delete ds.AcquisitionDate;
        delete ds.AcquisitionTime;
        delete ds.AcquisitionDateTime;
        // Describe the source's plane, not the resampled one.
        delete ds.InPlanePhaseEncodingDirection;
        delete ds.AcquisitionMatrix;

        // --- Study / series / FoR ---

        // Fallback only: `executeReformat` overwrites this with what the
        // user chose. Matches that default, since this writer always emits
        // an axial reformat.
        ds.SeriesDescription = DEFAULT_DERIVED_SERIES_DESCRIPTION;
        ds.StudyInstanceUID = studyInstanceUID;
        ds.SeriesInstanceUID = newSeriesInstanceUID;
        ds.FrameOfReferenceUID = frameOfReferenceUID;

        ds.SeriesNumber = options.seriesNumber;

        // --- SOP & common attributes ---

        ds.SOPClassUID = sopClassUID;
        ds.SOPInstanceUID = sopInstanceUID;
        ds.InstanceNumber = z + 1;

        // The shallow copy above carried the source's file-meta by
        // reference, which may declare an encapsulated transfer syntax
        // (e.g. JPEG Lossless). Our PixelData is raw, uncompressed,
        // little-endian Int16, so the derived slice must advertise an
        // uncompressed syntax — otherwise a re-read would try to
        // JPEG-decode raw pixels. Emit a fresh, non-shared _meta with
        // Explicit VR Little Endian and SOP UIDs matching this slice.
        ds._meta = {
            ...(exampleDataset._meta ?? {}),
            TransferSyntaxUID: {
                vr: 'UI',
                Value: [TransferSyntaxUID.ExplicitVRLittleEndian],
            },
            MediaStorageSOPClassUID: { vr: 'UI', Value: [sopClassUID] },
            MediaStorageSOPInstanceUID: { vr: 'UI', Value: [sopInstanceUID] },
        };

        // Type 1: refused rather than guessed, as SOPClassUID is. A
        // series labelled with the wrong modality misdirects every
        // consumer that switches on it.
        if (!exampleDataset.Modality) {
            throw new Error(
                'Cannot build derived series: source has no Modality'
            );
        }
        ds.Modality = exampleDataset.Modality;
        ds.ImageType = imageType;
        ds.DerivationDescription =
            options.derivationDescription ?? REFORMAT_DERIVATION_BASE;

        ds.Manufacturer = AppConfig.dicomManufacturer;
        ds.ManufacturerModelName = AppConfig.dicomManufacturerModelName;
        ds.SoftwareVersions = AppConfig.version;
        ds.SeriesDate = dateStr;
        ds.SeriesTime = timeStr;
        ds.InstanceCreationDate = dateStr;
        ds.InstanceCreationTime = timeStr;
        // Content is when these pixels came into being, which is now, not
        // when the source slice this template was copied from was made.
        ds.ContentDate = dateStr;
        ds.ContentTime = timeStr;

        // PatientPosition (0018,5100) records how the patient was
        // physically positioned on the table (HFS, FFS, ...). It must
        // be preserved from the source — only ImageOrientationPatient
        // and ImagePositionPatient change for the reformatted plane.
        ds.PatientPosition = exampleDataset.PatientPosition ?? '';

        // --- Geometry ---

        ds.Rows = rows;
        ds.Columns = columns;

        ds.ImageOrientationPatient = [
            roundToDecimals(rowDir[0], 8),
            roundToDecimals(rowDir[1], 8),
            roundToDecimals(rowDir[2], 8),
            roundToDecimals(colDir[0], 8),
            roundToDecimals(colDir[1], 8),
            roundToDecimals(colDir[2], 8),
        ];

        ds.ImagePositionPatient = [
            roundToDecimals(ipp[0], 8),
            roundToDecimals(ipp[1], 8),
            roundToDecimals(ipp[2], 8),
        ];

        ds.PixelSpacing = [
            rowSpacing.toFixed(4), // [0] = row spacing
            colSpacing.toFixed(4), // [1] = column spacing
        ];
        ds.SliceThickness = sliceThickness.toFixed(4);
        ds.SpacingBetweenSlices = sliceThickness.toFixed(4);
        ds.SliceLocation = roundToDecimals(
            vec3.dot(sliceNormal as vec3, ipp),
            4
        );

        // --- Rescale parameters ---
        // Both or neither: an output declaring one of the pair is an output
        // scaled by a transform only half applied.
        if (outputRescale === null) {
            delete ds.RescaleSlope;
            delete ds.RescaleIntercept;
        } else {
            ds.RescaleSlope = outputRescale.slope;
            ds.RescaleIntercept = outputRescale.intercept;
        }

        // --- Pixel module ---

        ds.SamplesPerPixel = samplesPerPixel;
        ds.PhotometricInterpretation = photometricInterpretation;
        ds.BitsAllocated = bitsAllocated;
        ds.BitsStored = bitsStored;
        ds.HighBit = highBit;
        ds.PixelRepresentation = pixelRepresentation;

        ds.PixelData = sliceBuffer;

        if (copiedFrom !== null) {
            // Not derived from an acquired image at all: it is a copy of
            // another slice of this same series. Naming the source image
            // here would claim a derivation that did not happen.
            ds.SourceImageSequence = [
                {
                    ReferencedSOPClassUID: sopClassUID,
                    ReferencedSOPInstanceUID: sopInstanceUIDs[copiedFrom],
                },
            ];
            ds.ImageComments = REPLICATED_SLICE_COMMENT;
        } else {
            // Deliberately absent on a resampled slice.
            //
            // Every output slice is interpolated from whichever source
            // slices its extent falls across, which is not one instance and
            // is generally not the same set as its neighbour's. Referencing
            // the template instance — the first slice of the source — named
            // an image that contributed nothing to most of the output and
            // left the ones that did contribute unreferenced, which is a
            // false provenance chain rather than a missing one. The
            // attribute is Type 3; the derivation is carried by ImageType
            // DERIVED and DerivationDescription.
            delete ds.SourceImageSequence;
        }

        datasets.push(ds);
    }

    return datasets;
}
